import { forwardRef, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ChevronsRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import { useSetCandidateStage } from "@/mutations/admin/set-candidate-stage";

import { AdminPipelineCandidate } from "@/queries/admin/candidate-pipeline";
import { CandidateWaitingRoomStatus } from "@/types";
import {
  candidateApprovalFormSchema,
  CandidateApprovalFormSchema,
} from "./schema";

const NEXT_STAGE_MAP: Record<
  CandidateWaitingRoomStatus,
  CandidateWaitingRoomStatus
> = {
  New: "Invited to Apply",
  "Invited to Apply": "Application Under Review",
  "Application Under Review": "Interviewing",
  Interviewing: "Interview Under Review",
  "Interview Under Review": "Ready For MAP",
  "Ready For MAP": "Taking/Sent MAP",
  "Taking/Sent MAP": "MAP Under Review",
  "MAP Under Review": "Joined",
  Joined: "Joined",
  "Dropped out": "Dropped out",
  Rejected: "Rejected",
};

type BaseButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

interface AdvanceCandidateButtonProps extends BaseButtonProps {
  candidate: AdminPipelineCandidate;
  onSuccess?: (data: AdminPipelineCandidate) => void;
}

export const AdvanceCandidateStatusButton = forwardRef<
  HTMLButtonElement,
  AdvanceCandidateButtonProps
>(({ candidate, onSuccess, ...buttonProps }, ref) => {
  const [open, setOpen] = useState(false);

  const successFn = useMemo(
    () => (data: AdminPipelineCandidate) => {
      setOpen(false);
      onSuccess?.(data);
    },
    [setOpen, onSuccess],
  );

  const { setCandidateStage, isSettingCandidateStage } =
    useSetCandidateStage(successFn);

  // Form only needed when schema exists
  const form = useForm<CandidateApprovalFormSchema>({
    resolver: zodResolver(candidateApprovalFormSchema),
    mode: "onChange",
  });

  const handleClick = (targetStage: CandidateWaitingRoomStatus) => {
    if (targetStage === "Joined") {
      setOpen(true);
    } else {
      setCandidateStage({
        candidateId: candidate.candidateId,
        stage: targetStage,
      });
    }
  };

  const onSubmit = (data: CandidateApprovalFormSchema) => {
    setCandidateStage({
      candidateId: candidate.candidateId,
      stage: "Joined",
      approvalData: data,
    });
  };

  return (
    <>
      <Button
        ref={ref}
        variant="secondary"
        {...buttonProps}
        disabled={isSettingCandidateStage || buttonProps.disabled}
        onClick={() => handleClick(NEXT_STAGE_MAP[candidate.stage])}
      >
        Advance <ChevronsRight />
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-full sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle>
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                  Approve {candidate.name || "A Candidate"}
                </DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Add additional information about the candidate to complete
                approval
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
              <Form {...form}>
                <form
                  className="space-y-4 w-full"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void form.handleSubmit((data) => onSubmit(data))();
                  }}
                >
                  <FormField
                    control={form.control}
                    name="cultureLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Culture Link</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://portal.talentinsights.com/report/abcdef"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="personalityResults"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Personality Result</FormLabel>
                        <FormControl>
                          <Input placeholder="ENFJ" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="personalityDetail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Personality Description</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Charismatic, empathetic, strategic, visionary"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="behaviorResults"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Behavior Result</FormLabel>
                        <FormControl>
                          <Input placeholder="Support & Influence" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="behaviorDetail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Behavior Description</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Balances relationships with a focus on harmony and assertiveness"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="motivatorsResults"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Motivators Result</FormLabel>
                        <FormControl>
                          <Input placeholder="Enterprising" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="motivatorsDetails"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Motivators Description</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enjoys utilizing strong leadership and initiative-taking capabilities"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="w-full flex justify-between">
                    <Button
                      variant="outline"
                      className="w-fit"
                      type="button"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-fit"
                      type="submit"
                      disabled={
                        !form.formState.isValid || isSettingCandidateStage
                      }
                    >
                      Approve
                    </Button>
                  </div>
                </form>
              </Form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
AdvanceCandidateStatusButton.displayName = "AdvanceCandidateStatusButton";
